export const EDITOR_RESTORE_STATE_STORAGE_KEY = "pending-editor-restore-state";

export type EditorRestoreSessionStatus = "pending" | "done";

interface PersistedRestoreState {
  restoreRequestId?: string;
}

export const getRestoreSessionKey = (requestId: string): string =>
  `editor-upload-restore:${requestId}`;

export const getRestoreSessionStatus = (
  requestId?: string | null,
): EditorRestoreSessionStatus | null => {
  if (typeof window === "undefined" || !requestId) return null;
  const raw = sessionStorage.getItem(getRestoreSessionKey(requestId));
  return raw === "pending" || raw === "done" ? raw : null;
};

export const isRestoreSessionPending = (
  requestId?: string | null,
): boolean => getRestoreSessionStatus(requestId) === "pending";

export const isRestoreSessionDone = (
  requestId?: string | null,
): boolean => getRestoreSessionStatus(requestId) === "done";

export const markRestoreSessionPending = (requestId: string): void => {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(getRestoreSessionKey(requestId), "pending");
};

export const markRestoreSessionDone = (requestId: string): void => {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(getRestoreSessionKey(requestId), "done");
};

export const clearPersistedEditorRestoreState = (
  requestId?: string | null,
): void => {
  if (typeof window === "undefined") return;

  if (!requestId) {
    sessionStorage.removeItem(EDITOR_RESTORE_STATE_STORAGE_KEY);
    return;
  }

  const persistedRestoreState = sessionStorage.getItem(
    EDITOR_RESTORE_STATE_STORAGE_KEY,
  );
  if (!persistedRestoreState) return;

  try {
    const parsed = JSON.parse(persistedRestoreState) as PersistedRestoreState;
    if (parsed.restoreRequestId === requestId) {
      sessionStorage.removeItem(EDITOR_RESTORE_STATE_STORAGE_KEY);
    }
  } catch {
    sessionStorage.removeItem(EDITOR_RESTORE_STATE_STORAGE_KEY);
  }
};

export const clearEditorRestoreSession = (
  requestId?: string | null,
): void => {
  if (typeof window === "undefined") return;

  if (requestId) {
    sessionStorage.removeItem(getRestoreSessionKey(requestId));
  }
  clearPersistedEditorRestoreState(requestId);
};
